import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Plus, Search, Users } from "lucide-react";

export default function Customers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", pricing_group: "", password: "" });

  const load = useCallback(async () => {
    const { data } = await api.get("/store/customers", { params: { search: search || undefined } });
    setCustomers(data);
  }, [search]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const create = async () => {
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    try {
      await api.post("/store/customers", { ...form, password: form.password || null });
      toast.success("Customer added");
      setOpen(false);
      setForm({ name: "", email: "", phone: "", pricing_group: "", password: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Customers</h1>
          <p className="text-muted-foreground mt-1">Your CRM — pricing, sales, and notes.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-lg" data-testid="add-customer-btn"><Plus className="h-4 w-4 mr-1.5" /> Add customer</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add customer</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="customer-name-input" /></div>
              <div className="space-y-2"><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="customer-email-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} data-testid="customer-phone-input" /></div>
                <div className="space-y-2"><Label>Pricing group</Label><Input value={form.pricing_group} onChange={(e) => setForm((f) => ({ ...f, pricing_group: e.target.value }))} placeholder="e.g. wholesale" data-testid="customer-group-input" /></div>
              </div>
              <div className="space-y-2">
                <Label>Login password (optional)</Label>
                <Input type="text" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="Set to create login credentials" data-testid="customer-password-input" />
                <p className="text-xs text-muted-foreground">If set, the customer can log in to your storefront.</p>
              </div>
            </div>
            <DialogFooter><Button onClick={create} data-testid="customer-create-btn">Add customer</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search customers..." className="pl-9 h-11" data-testid="customer-search-input" />
      </div>

      <Card className="rounded-xl border-border overflow-hidden">
        {customers.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
            <p className="font-medium">No customers yet</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Group</TableHead>
                <TableHead className="text-right">Orders</TableHead>
                <TableHead className="text-right">Lifetime sales</TableHead>
                <TableHead>Last order</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customers.map((c) => (
                <TableRow key={c.id} className="cursor-pointer" onClick={() => navigate(`/dashboard/customers/${c.id}`)} data-testid={`customer-row-${c.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm shrink-0">{c.name?.[0]?.toUpperCase()}</div>
                      <div><p className="font-medium">{c.name}</p><p className="text-xs text-muted-foreground">{c.email}</p></div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{c.pricing_group || "—"}</TableCell>
                  <TableCell className="text-right">{c.total_orders}</TableCell>
                  <TableCell className="text-right font-semibold">{money(c.lifetime_sales)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{c.last_order_at ? new Date(c.last_order_at).toLocaleDateString() : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
